import { Link } from '@tanstack/react-router'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { secondaryLinkClasses } from '@workspace/ui/components/dsm/secondary-button'
import { Check, Copy, Linkedin, Pencil, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useGenerateContactMessageMutation,
  useUpdateStartupOutreachBodyMutation,
} from '../../../hooks/api'
import { trackEvent } from '../../../lib/gtag'

interface Contact {
  id: string
  name: string
  title: string | null
  linkedinUrl: string | null
  scope: string | null
  order: number
}

interface ContactMessageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact: Contact
  startupId: string
  investorId: string
  investorName: string
  websiteUrl: string | null
  evaluationContext: string
  startupName: string
  startupDescription?: string | null
  savedBody: string | null
  onBodySaved: (body: string) => void
  savedHook: string | null
  onHookSaved: (hook: string) => void
  onOpenLinkedIn: (contact: Contact) => void
  source: 'evaluation' | 'matches'
  // Startup default share-link URL — appended at copy/send time so the body
  // text we display and store stays URL-free.
  shareUrl?: string | null
}

const MAX_CHARS = 300

export function ContactMessageDialog({
  open,
  onOpenChange,
  contact,
  startupId,
  investorId,
  investorName,
  websiteUrl,
  shareUrl,
  evaluationContext,
  startupName,
  startupDescription,
  savedBody,
  onBodySaved,
  savedHook,
  onHookSaved,
  onOpenLinkedIn,
  source,
}: ContactMessageDialogProps) {
  const [copied, setCopied] = useState(false)
  const [hook, setHookState] = useState<string | null>(savedHook)
  const setHook = useCallback(
    (next: string | null) => {
      setHookState(next)
      if (next != null) onHookSaved(next)
    },
    [onHookSaved]
  )
  const [body, setBody] = useState<string>(savedBody ?? '')
  const [hookPending, setHookPending] = useState(false)
  const [bodyPending, setBodyPending] = useState(false)
  // Set when the backend returns 403 — investor isn't in the unlocked match
  // set. Rendered as an in-dialog paywall notice instead of leaving the
  // textareas empty.
  const [locked, setLocked] = useState(false)

  const messageMutation = useGenerateContactMessageMutation()
  // Separate mutation instance so debounced hook auto-saves never cancel an
  // in-flight generation (mutate() on the same hook replaces the previous
  // call and its onSuccess never fires).
  const persistMutation = useGenerateContactMessageMutation()
  const saveBodyMutation = useUpdateStartupOutreachBodyMutation(startupId)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hookDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced persistence of a manually-edited hook for this specific
  // contact. Calls the contact-message endpoint with both generate flags
  // false; the engine's legacy partial-regen path upserts (hook, body)
  // into contact_outreach_messages without invoking any LLM.
  const persistHookEdit = useCallback(
    (nextHook: string) => {
      if (!nextHook || !body) return
      persistMutation.mutate({
        path: { startupId, investorId },
        body: {
          contactId: contact.id,
          investorName,
          evaluationContext,
          startupName,
          startupDescription: startupDescription ?? null,
          generateHook: false,
          generateBody: false,
          existingHook: nextHook,
          existingBody: body,
          force: false,
        },
      })
    },
    [
      startupId,
      investorId,
      contact.id,
      investorName,
      evaluationContext,
      startupName,
      startupDescription,
      body,
      persistMutation,
    ]
  )

  const linkedinUrl = contact.linkedinUrl ?? websiteUrl

  const generateHook = useCallback(
    (force = false) => {
      trackEvent('investor_hook_regenerated', {
        startup_id: startupId,
        investor_id: investorId,
        contact_id: contact.id,
      })
      setHookPending(true)
      messageMutation.mutate(
        {
          path: { startupId, investorId },
          body: {
            contactId: contact.id,
            investorName,
            evaluationContext,
            startupName,
            startupDescription: startupDescription ?? null,
            generateHook: true,
            generateBody: false,
            existingBody: body || null,
            force,
          },
        },
        {
          onSuccess: (data) => {
            if (data.status === 'ok') setHook(data.data.hook)
            setHookPending(false)
          },
          onError: (err: unknown) => {
            setHookPending(false)
            if (
              typeof err === 'object' &&
              err !== null &&
              'status' in err &&
              (err as { status?: number }).status === 403
            ) {
              setLocked(true)
            }
          },
        }
      )
    },
    [
      startupId,
      investorId,
      contact.id,
      investorName,
      evaluationContext,
      startupName,
      startupDescription,
      body,
      messageMutation,
      setHook,
    ]
  )

  const generateBody = useCallback(
    (force = false) => {
      trackEvent('investor_body_regenerated', {
        startup_id: startupId,
        investor_id: investorId,
        contact_id: contact.id,
      })
      setBodyPending(true)
      const alsoGenerateHook = !hook
      if (alsoGenerateHook) setHookPending(true)
      messageMutation.mutate(
        {
          path: { startupId, investorId },
          body: {
            contactId: contact.id,
            investorName,
            evaluationContext,
            startupName,
            startupDescription: startupDescription ?? null,
            generateHook: alsoGenerateHook,
            generateBody: true,
            existingHook: hook || null,
            force,
          },
        },
        {
          onSuccess: (data) => {
            if (data.status === 'ok') {
              setBody(data.data.body)
              if (alsoGenerateHook && data.data.hook) setHook(data.data.hook)
              saveBodyMutation.mutate(
                { path: { startupId }, body: { body: data.data.body } },
                { onSuccess: () => onBodySaved(data.data.body) }
              )
            }
            setBodyPending(false)
            if (alsoGenerateHook) setHookPending(false)
          },
          onError: (err: unknown) => {
            setBodyPending(false)
            if (alsoGenerateHook) setHookPending(false)
            if (
              typeof err === 'object' &&
              err !== null &&
              'status' in err &&
              (err as { status?: number }).status === 403
            ) {
              setLocked(true)
            }
          },
        }
      )
    },
    [
      startupId,
      investorId,
      contact.id,
      investorName,
      evaluationContext,
      startupName,
      startupDescription,
      hook,
      messageMutation,
      saveBodyMutation,
      onBodySaved,
      setHook,
    ]
  )

  // Sync to saved values / kick off generation whenever the dialog opens or
  // the active contact changes.
  useEffect(() => {
    if (!open) return
    trackEvent('investor_contact_clicked', {
      startup_id: startupId,
      investor_id: investorId,
      contact_id: contact.id,
      source,
    })
    setHookState(savedHook ?? null)
    setBody(savedBody ?? '')
    setLocked(false)
    if (!savedBody) {
      // generateBody also generates the hook in a single call when hook is missing,
      // avoiding a race between two parallel mutations.
      generateBody()
    } else if (!savedHook) {
      generateHook()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact.id])

  // Debounced auto-save on body edit
  const handleBodyChange = (value: string) => {
    setBody(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      saveBodyMutation.mutate(
        { path: { startupId }, body: { body: value } },
        { onSuccess: () => onBodySaved(value) }
      )
    }, 1000)
  }

  const urlSuffix = shareUrl ? ` ${shareUrl}` : ''
  // Body in storage is URL-free — append the share link only when assembling
  // the message for copy/send. If `body` already ends with the share URL
  // (legacy data, or upstream that hasn't been migrated yet), don't double it.
  const bodyWithUrl =
    body && shareUrl && !body.trimEnd().endsWith(shareUrl) ? `${body}${urlSuffix}` : body
  const fullMessage = hook && body ? `${hook.replace(/\.+$/, '')}. ${bodyWithUrl}` : ''
  const isOverLimit = fullMessage.length > MAX_CHARS
  const canCopy = !!hook && !!body && !hookPending && !bodyPending && !isOverLimit

  const handleCopyAndOpen = async () => {
    if (!canCopy) return
    await navigator.clipboard.writeText(fullMessage)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    trackEvent('investor_message_copied', {
      startup_id: startupId,
      investor_id: investorId,
      contact_id: contact.id,
    })
    trackEvent('investor_linkedin_opened', {
      startup_id: startupId,
      investor_id: investorId,
      contact_id: contact.id,
    })
    onOpenLinkedIn(contact)
    if (linkedinUrl) window.open(linkedinUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-base leading-snug">
            Message for {contact.name}
            {contact.title ? (
              <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                · {contact.title}
              </span>
            ) : null}
            {contact.linkedinUrl ? (
              <a
                href={contact.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1.5 inline-flex translate-y-[1px] items-center text-muted-foreground/50 transition-colors hover:text-primary"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin className="size-3.5" />
              </a>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {locked ? (
          <div className="mt-2 rounded-lg border border-border/60 bg-muted/30 p-4 text-center text-sm text-muted-foreground">
            <p className="font-medium text-foreground">This investor is locked</p>
            <p className="mt-1 text-xs">
              Unlock all matches on the previous screen to generate and send messages to this
              contact.
            </p>
          </div>
        ) : (
          <div className="mt-2 space-y-3">
            {/* Hook section */}
            <div>
              <textarea
                className="w-full resize-none rounded-lg border border-border/60 bg-muted/30 p-3 text-sm leading-relaxed text-foreground outline-none transition-colors focus:border-primary/60 focus:bg-muted/50 disabled:opacity-60"
                rows={3}
                value={hook ?? ''}
                onChange={(e) => {
                  const next = e.target.value
                  setHook(next)
                  if (hookDebounceRef.current) clearTimeout(hookDebounceRef.current)
                  hookDebounceRef.current = setTimeout(() => persistHookEdit(next), 1000)
                }}
                placeholder={hookPending ? 'Generating hook…' : 'Hey …'}
                disabled={hookPending}
              />
              <div className="mt-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => generateHook(true)}
                  disabled={hookPending}
                  className="flex items-center gap-1 text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground disabled:opacity-40"
                >
                  <RefreshCw className="size-3" />
                  Regenerate
                </button>
              </div>
            </div>

            {/* Body section */}
            <div>
              <textarea
                className="w-full resize-none rounded-lg border border-border/60 bg-muted/30 p-3 text-sm leading-relaxed text-foreground outline-none transition-colors focus:border-primary/60 focus:bg-muted/50 disabled:opacity-60"
                rows={6}
                value={body}
                onChange={(e) => handleBodyChange(e.target.value)}
                placeholder={bodyPending ? 'Generating body…' : 'Body message…'}
                disabled={bodyPending}
              />
              <div className="mt-1 flex items-center justify-between">
                <span
                  className={`text-xs ${isOverLimit ? 'text-destructive' : 'text-muted-foreground/60'}`}
                >
                  {fullMessage.length} / {MAX_CHARS} characters
                </span>
                <button
                  type="button"
                  onClick={() => generateBody(true)}
                  disabled={bodyPending}
                  className="flex items-center gap-1 text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground disabled:opacity-40"
                >
                  <RefreshCw className="size-3" />
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        <Link
          to="/app/startup/$id/profile"
          params={{ id: startupId }}
          className={secondaryLinkClasses({ size: 'sm', fullWidth: true })}
          onClick={() =>
            trackEvent('investor_profile_edit_clicked', {
              startup_id: startupId,
              investor_id: investorId,
              contact_id: contact.id,
            })
          }
        >
          <Pencil />
          <span>Edit the profile investors will see</span>
        </Link>

        {!locked && (
          <div className="space-y-1.5">
            {shareUrl && (
              <p className="px-1 pb-2 text-center text-[11px] leading-snug text-muted-foreground/60">
                Share link appended automatically:{' '}
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-foreground/80 underline-offset-2 hover:underline"
                >
                  {shareUrl}
                </a>
              </p>
            )}
            <CtaButton
              size="sm"
              fullWidth
              icon={
                copied ? (
                  <Check />
                ) : (
                  <>
                    <Copy />
                    <Linkedin />
                  </>
                )
              }
              onClick={handleCopyAndOpen}
              disabled={!canCopy}
            >
              {copied ? 'Copied!' : 'Copy entire message & open LinkedIn'}
            </CtaButton>
          </div>
        )}

        {copied && (
          <p className="text-xs text-muted-foreground/50">
            Paste the message into the LinkedIn connection request note.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
